import { Inject, Injectable } from "@angular/core";
import { BehaviorSubject, Observable, catchError, map, switchMap, take, tap } from "rxjs";

import type {
    ApiResponse,
    BorrowUserRequest,
    RepayUserLoanRequest,
    UserLoanRequest,
    BorrowUserResponse,
    LoanInfo,
    LoanInfoResponce,
    UserLoanByCoin,
    CurrencyType,
    MergedLoanCoin
} from '@coin-depo-types';

import { ApiService } from "@coin-depo/http";
import { CoinFacade, COIN_FACADE } from '@coin-depo-core/coin';
import { StorageService } from '@coin-depo-core/storage';

import { BorrowStatsFacade } from '@coin-depo-core/borrow-stats';
import { AUTH_SERVICE_TOKEN, AuthFacade } from "@coin-depo-core/auth";

@Injectable({
    providedIn: "root"
})
export class BorrowStatsService implements BorrowStatsFacade {
    private borrowsByCoin$ = new BehaviorSubject<UserLoanByCoin[]>(
       this.storage.get('loans_by_coin', []) 
    )
    cachedBorrowsByCoin$ = this.borrowsByCoin$.asObservable()

    private borrowCreditLineLimit$ = new BehaviorSubject<string>(
        this.storage.get('borrowCreditLineLimit', '0') 
    )
    cachedBorrowCreditLineLimit$ = this.borrowCreditLineLimit$.asObservable()

    private currentLoans$ = new BehaviorSubject<string>(
        this.storage.get('currentUserLoans', '0')
    )
    cachedCurrentLoans$ = this.currentLoans$.asObservable()

    private borrowAvailableCreditLimit$ = new BehaviorSubject<string>(
        this.storage.get('borrowAvailableCreditLimit', '0')
    )
    cachedBorrowAvailableCreditLimit$ = this.borrowAvailableCreditLimit$.asObservable()

    private borrowLtv$ = new BehaviorSubject<number>(
        this.storage.get('ltv', 0)
    )
    cachedBorrowLtv$ = this.borrowLtv$.asObservable()

    private totalAvailableBalance$ = new BehaviorSubject<string>(
        this.storage.get('borrowStats.total_available_balance')
    )
    cachedTotalAvailableBalance$ = this.totalAvailableBalance$.asObservable()

    private totalUserLoans$ = new BehaviorSubject<string>(
        this.storage.get('borrowStats.totalUserLoansCurrency', '0')
    );
    cachedTotalUserLoans$ = this.totalUserLoans$.asObservable();

    private mergedLoansOfUser$ = new BehaviorSubject<MergedLoanCoin[]>(
        this.storage.get('borrowStats.mergedLoansOfUser', [])
    );
    cachedMergedLoansOfUser$ = this.mergedLoansOfUser$.asObservable();

    constructor(
         @Inject(AUTH_SERVICE_TOKEN) private auth: AuthFacade,
        private storage: StorageService,
        private borrowApi: ApiService,
        @Inject(COIN_FACADE) private coinsService: CoinFacade,
    ) {}

    getLoanStats(currency: CurrencyType): Observable<MergedLoanCoin[]> {
        const data: BorrowUserRequest = {
            client_token: this.auth.accessToken,
            username: this.auth.userData.email,
            currency
        };

        return this.borrowApi.getLoanStats(data).pipe(
            take(1),
            switchMap((res: LoanInfoResponce) => {
              const loans: LoanInfo[] = res.loans;
              this.totalUserLoans$.next(res.total_loans_currency);
      
              return this.coinsService.cachedSupportedCoins$.pipe(
                map(coins => {
                  const mergedBorrows = loans.map(loan => {
                    const findedCoin = coins.find(coin => loan.coin_id === coin.coin_id)
                    return {
                      ...findedCoin, 
                      ...loan
                    }
                  }) as MergedLoanCoin[];

                  this.mergedLoansOfUser$.next(mergedBorrows)
                  
                  return mergedBorrows
                })
              )
            }),
        );
    }

    getBorrowInfo(currency: CurrencyType): Observable<BorrowUserResponse> {
        const data: BorrowUserRequest = {
            client_token: this.auth.accessToken,
            username: this.auth.userData.email,
            currency
        };

        return this.borrowApi.userInfoBorrow(data).pipe(
            // @ts-ignore
            catchError((err: { msg: string }) => {
                console.error(err.msg);
                return err
            }),
            tap((res: any) => {
                console.log(res);
                this.setBorrowStates(res);
            })
        )
    }

    getUserLoan(amount: number, coin_id: number): Observable<ApiResponse> {
        const data: UserLoanRequest = {
            client_token: this.auth.accessToken,
            username: this.auth.userData.email,
            amount,
            coin_id,
        };

        return this.borrowApi.getUserLoan(data)
    }

    repayUserLoan(amount: number, loan_id: number): Observable<ApiResponse> {
        const data: RepayUserLoanRequest ={
            client_token: this.auth.accessToken,
            username: this.auth.userData.email,
            amount,
            loan_id,
        }

        return this.borrowApi.repayUserLoan(data)
    }

    private setBorrowStates(borrowState: BorrowUserResponse) {
        this.setCreditLineLimit(borrowState.credit_line_limit_currency)
        this.setCurrentLoans(borrowState.current_loans_currency)
        this.setAvailableCreditLemit(borrowState.available_credit_limit_currency)
        this.setLoansByCoin(borrowState.loans_by_coin, borrowState.aprs, borrowState.bonus_aprs)
        this.setLtv(Number(borrowState.ltv))
        this.setTotalAvailableBalance(borrowState.available_portfolio_balance)
    }

    private setCreditLineLimit(credit_line_limit: string) {
        this.borrowCreditLineLimit$.next(credit_line_limit);
        this.storage.set(
            'borrowCreditLineLimit',
            credit_line_limit
          );
    }

    private setCurrentLoans(current_loan: string) {
        this.currentLoans$.next(current_loan)
        this.storage.set(
            'currentUserLoans',
             current_loan   
        );
    }

    private setAvailableCreditLemit(available_credit_limit: string) {
        this.borrowAvailableCreditLimit$.next(available_credit_limit)
        this.storage.set(
            'borroeAvailableCreditLemit',
            available_credit_limit
        );
    }

    private setLoansByCoin(loans_by_coin: UserLoanByCoin[], aprs: string[], bonus_aprs: string[]) {
        const updatedLoans = loans_by_coin.map(loan => ({
            ...loan,
            aprs,
            bonus_aprs
        }));

        this.borrowsByCoin$.next(updatedLoans)
        this.storage.set(
            'borrowLoansByCoin',
            updatedLoans
        )
    }

    private setLtv(ltv: number) {
        this.borrowLtv$.next(ltv)
        this.storage.set(
            'ltv',
            ltv
        )
    }

    private setTotalAvailableBalance(total_available_balance: string) {
        this.totalAvailableBalance$.next(total_available_balance);
        this.storage.set(
          'borrowStats.total_available_balance',
          total_available_balance
        );
      }
}
